import {FC, ReactNode, useEffect, useId, useRef} from 'react'
import {Link} from 'react-router-dom'
import {Modal, Toast} from 'react-bootstrap'
import clsx from 'clsx'
import {businessSettingsPath} from '../../online-payment/core/businessSettingsPath'
import {WalletResumeStep, WalletShortfallAction} from '../../online-payment/wallet/WalletTopUp'
import {ServiceBadge} from './ServiceBadge'
import {ServiceRequestLink} from './SupportContact'
import {ServiceIntroLink} from './ServiceIntroLink'
import {useAuth} from '../../auth'
import {hidesPayments, STORE_BUILD_UNAVAILABLE_TEXT} from '../../install-app/core/androidApp'
import {StoreBuildUnavailableDialog} from '../../online-payment/components/StoreBuildUnavailable'
import {
  activationTitle,
  ChargeItem,
  chargeKind,
  contactSentence,
  fa,
  isContactTariff,
  isSmsClosed,
  needsContact,
  ServiceError,
  ServiceFee,
  ServiceSms,
  ServiceTariff,
  SmsOutcome,
  tariffParts,
  tariffPricingNote,
  WALLET_INSUFFICIENT,
} from '../core/serviceApi'

// ---- states ----------------------------------------------------------------------------------------------

export const ServiceLoading: FC<{text: string}> = ({text}) => (
  <div
    className='ops-card d-flex align-items-center gap-2 text-muted'
    role='status'
    aria-live='polite'
  >
    <span className='spinner-border spinner-border-sm' aria-hidden='true'></span>
    {text}
  </div>
)

/** The service's tables are not in place yet (503): a calm note, not an error. */
export const ServiceUnavailable: FC<{title: string}> = ({title}) => (
  <div className='ops-empty bsv-soon' role='status'>
    <i className='las la-hourglass-half fs-2x' aria-hidden='true'></i>
    <strong>{title}</strong>
    <span>این سرویس به‌زودی فعال می‌شود.</span>
  </div>
)

export const ServiceErrorAlert: FC<{message: string; onRetry: () => void}> = ({
  message,
  onRetry,
}) => (
  <div className='alert alert-danger d-flex flex-wrap align-items-center gap-3' role='alert'>
    <span className='flex-grow-1'>{message}</span>
    <button type='button' className='btn btn-sm btn-light-danger' onClick={onRetry}>
      تلاش مجدد
    </button>
  </div>
)

export type Notice = {
  tone: 'success' | 'danger' | 'warning'
  text: string
  walletLink?: boolean
  /** The wallet's balance and what the step needed (a 409 WALLET_INSUFFICIENT_BALANCE): the shortfall to charge. */
  balance?: number
  required?: number
  /** «افزایش موجودی و …»: the step's name on the button, and the step to go on with after the payment. */
  walletAction?: string
  walletResume?: WalletResumeStep
  /** A «تماس بگیرید» service with no access (409 BUSINESS_SERVICE_CONTACT_REQUIRED): its request follows. */
  contact?: boolean
  /** That service's key, and whether its access ended (a renewal is asked for then). */
  contactFeature?: string
  contactEnded?: boolean
}

/**
 * A save's result. A short wallet carries «افزایش موجودی و …» with its shortfall (user request 2026-10-17); with
 * `wallet.resume` the save goes on by itself after the payment. A «تماس بگیرید» service carries the support line
 * instead: no fee to confirm and nothing to charge. In the Bazaar and Myket builds of the Android app neither: the
 * step is only said to be unavailable there (hidesPayments).
 */
export const noticeFromError = (error: ServiceError, wallet?: {action?: string; resume?: WalletResumeStep}): Notice => {
  if (hidesPayments() && (error.code === WALLET_INSUFFICIENT || error.contact))
    return {tone: 'warning', text: STORE_BUILD_UNAVAILABLE_TEXT}
  return {
    tone: 'danger',
    text: error.contact ? error.contact.text : error.message,
    walletLink: error.code === WALLET_INSUFFICIENT,
    balance: error.balance,
    required: error.required,
    walletAction: wallet?.action,
    walletResume: wallet?.resume,
    contact: Boolean(error.contact),
    contactFeature: error.contact?.feature,
    contactEnded: error.contact?.ended,
  }
}

export const NoticeLine: FC<{notice?: Notice; companyId: number; className?: string}> = ({
  notice,
  companyId,
  className,
}) =>
  notice ? (
    <div
      className={clsx(
        `alert alert-${notice.tone} py-3 mb-0 d-flex flex-wrap align-items-center gap-2`,
        className
      )}
      role={notice.tone === 'success' ? 'status' : 'alert'}
    >
      <span className='flex-grow-1'>{notice.text}</span>
      {notice.walletLink && (
        <WalletShortfallAction
          companyId={companyId}
          balance={notice.balance}
          required={notice.required}
          actionLabel={notice.walletAction}
          resume={notice.walletResume}
        />
      )}
      {notice.contact && (
        <ServiceRequestLink
          topic={notice.contactEnded ? 'AccessRenewal' : 'ServiceActivation'}
          feature={notice.contactFeature}
        />
      )}
    </div>
  ) : null

// ---- SMS cost and tariffs ----------------------------------------------------------------------------------

const TARIFF_MODE_TITLES: Record<string, string> = {
  free: 'رایگان',
  oneTime: 'فعال‌سازی یک‌باره',
  perUse: 'هر بار استفاده',
  combined: 'اشتراک یا هر بار استفاده',
}

export const tariffText = (tariff: ServiceTariff): string => {
  if (tariff.description?.trim()) return tariff.description.trim()
  if (isContactTariff(tariff)) {
    return tariff.freeQuotaText ? `فعال‌سازی با درخواست؛ ${tariff.freeQuotaText}` : 'فعال‌سازی با درخواست'
  }
  if (tariff.mode === 'free') return 'رایگان'
  const parts = tariffParts(tariff)
  // «ترکیبی»: one of the two, never both (2026-10-20).
  if (tariff.mode === 'combined')
    return `${activationTitle(parts.period)}: ${fa(parts.activation)} ریال یا هر بار استفاده: ${fa(parts.use)} ریال`
  return `${TARIFF_MODE_TITLES[tariff.mode] || ''}: ${fa(tariff.amount)} ریال`
}

/** The tariffs ASA set for the service's features, separate from the SMS cost. */
export const TariffList: FC<{tariffs?: ServiceTariff[] | null}> = ({tariffs}) => {
  if (!tariffs?.length || hidesPayments()) return null
  return (
    <div className='bsv-tariffs'>
      <h4 className='bsv-subtitle d-flex align-items-center gap-2'>
        هزینهٔ سرویس <ServiceBadge />
      </h4>
      <ul>
        {tariffs.map((tariff) => (
          <li key={tariff.feature}>
            <span className='bsv-tariffs__label'>
              {tariff.label} <ServiceIntroLink feature={tariff.feature} label={tariff.label} iconOnly />
            </span>
            {tariff.mode !== 'free' && tariff.listAmount && tariff.listAmount > tariff.amount ? (
              <span className='bsv-tariffs__offer'>
                <s aria-label={`قیمت قبل از تخفیف ${fa(tariff.listAmount)} ریال`}>{fa(tariff.listAmount)}</s>
                <span className='svc-price__off'>
                  {fa(Math.round(((tariff.listAmount - tariff.amount) / tariff.listAmount) * 100))}٪ تخفیف
                </span>
              </span>
            ) : null}
            <span
              className={clsx(
                'badge',
                tariff.mode === 'free' ? 'badge-light-success' : 'badge-light-primary'
              )}
            >
              {tariffText(tariff)}
            </span>
            {tariffParts(tariff).activation > 0 && tariff.activated && !isContactTariff(tariff) && (
              <span className='text-muted fs-8'>
                {tariff.expiresAtLabel
                  ? `اعتبار تا ${tariff.expiresAtLabel}`
                  : `پرداخت شده${tariff.activatedAtLabel ? ` در ${tariff.activatedAtLabel}` : ''}`}
              </span>
            )}
            {isContactTariff(tariff) && tariff.granted && (
              <span className='text-muted fs-8'>
                {tariff.expiresAtLabel ? `فعال تا ${tariff.expiresAtLabel}` : 'فعال با دسترسی پشتیبانی'}
              </span>
            )}
            {tariff.expired && !isContactTariff(tariff) && (
              <span className='text-danger fs-8 fw-semibold'>اشتراک تمام شده؛ برای ادامه تمدید کنید</span>
            )}
            {needsContact(tariff) && (
              // Where «فعال‌سازی» would be: the server's sentence and the request for the service.
              <span className='bsv-tariffs__pricing bsv-tariffs__contact'>
                <span>{contactSentence(tariff)}</span>
                <ServiceRequestLink
                  topic={tariff.expired ? 'AccessRenewal' : 'ServiceActivation'}
                  feature={tariff.feature}
                  label={tariff.label}
                />
              </span>
            )}
            {tariffPricingNote(tariff) && (
              <span className='bsv-tariffs__pricing'>
                <i className='las la-gift' aria-hidden='true'></i>
                {tariffPricingNote(tariff)}
              </span>
            )}
            {tariff.package && (
              <span className='bsv-tariffs__pricing'>
                <i className='las la-box' aria-hidden='true'></i>
                در {tariff.package.titles.map((title) => `«${title}»`).join('، ')}
                {tariff.package.coversActivation ? '، بدون هزینهٔ فعال‌سازی' : ''}
                {tariff.package.includedText ? `، ${tariff.package.includedText}` : ''}
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className='text-muted fs-8 mb-0'>
        هزینهٔ سرویس جدا از هزینهٔ پیامک است و از همان کیف پول کم می‌شود.
      </p>
    </div>
  )
}

/**
 * «هزینهٔ پیامک و کیف پول»: the price of an SMS part and the wallet's balance, «بدون کسر از کیف پول» for a
 * business that does not pay for SMS, the closed state while no price is set, and the service tariffs.
 */
export const SmsCostCard: FC<{
  companyId: number
  sms: ServiceSms
  tariffs?: ServiceTariff[] | null
  freeChannelsNote: string
  id?: string
  className?: string
  children?: ReactNode
}> = ({companyId, sms, tariffs, freeChannelsNote, id, className, children}) =>
  // Prices and the wallet: none in the Bazaar and Myket builds of the Android app.
  hidesPayments() ? null : (
    <section id={id} className={clsx('ops-card', className)} aria-labelledby={`bsv-cost-${companyId}`}>
      <h3 className='ops-card__title' id={`bsv-cost-${companyId}`}>
        هزینه و کیف پول
      </h3>
      <p className='text-muted fs-7'>{freeChannelsNote}</p>
      {sms.withoutCharge ? (
        <div className='alert alert-success py-3 mb-3'>
          پیامک‌های این کسب‌وکار بدون کسر از کیف پول فرستاده می‌شود.
        </div>
      ) : isSmsClosed(sms) ? (
        <div className='alert alert-warning py-3 mb-3'>
          هزینهٔ پیامک هنوز تعیین نشده است؛ فعلاً فقط اعلان داخل پنل فرستاده می‌شود.
        </div>
      ) : (
        <dl className='bl-facts'>
          <dt>هزینهٔ هر بخش پیامک</dt>
          <dd>{fa(sms.partPrice)} ریال</dd>
          <dt>موجودی کیف پول</dt>
          <dd>{fa(sms.balance)} ریال</dd>
        </dl>
      )}
      {sms.withoutCharge && (
        <dl className='bl-facts'>
          <dt>موجودی کیف پول</dt>
          <dd>{fa(sms.balance)} ریال</dd>
        </dl>
      )}
      <TariffList tariffs={tariffs} />
      {children}
      {sms.chargeEnabled && (
        <Link
          className='btn btn-light-primary btn-sm mt-3'
          to={businessSettingsPath(companyId, 'wallet')}
        >
          <i className='las la-wallet me-1' aria-hidden='true'></i>
          شارژ کیف پول
        </Link>
      )}
    </section>
  )

// ---- SMS preview -------------------------------------------------------------------------------------------

/**
 * The SMS as it reaches a phone, with its parts and its cost for one recipient and for all of them.
 * `loading` keeps the previous text, dimmed, while a new preview is built.
 */
export const SmsBubble: FC<{
  body?: string | null
  parts?: number
  sms?: ServiceSms
  recipients?: number
  loading?: boolean
  label?: string
}> = ({body, parts, sms, recipients, loading, label = 'پیش‌نمایش پیامک'}) => {
  const perSms =
    sms && parts !== undefined ? (sms.withoutCharge ? 0 : parts * sms.partPrice) : undefined
  const count = Math.max(0, recipients || 0)
  return (
    <div className={clsx('bsv-sms', {'is-loading': loading})} aria-busy={loading}>
      <div className='bsv-phone' role='group' aria-label={label}>
        {body ? (
          // The SMS itself carries Latin digits (the report SMS rule); the panel font would draw them Persian.
          <div className='bsv-phone__bubble englishText'>{body}</div>
        ) : (
          <div className='bsv-phone__empty'>
            {loading ? 'در حال ساخت پیش‌نمایش…' : 'پیش‌نمایشی در دسترس نیست.'}
          </div>
        )}
      </div>
      {parts !== undefined && (
        <div className='bsv-sms__cost'>
          <span>
            <i className='las la-layer-group' aria-hidden='true'></i> {fa(parts)} بخش
          </span>
          {sms?.withoutCharge ? (
            <span className='text-success'>
              <i className='las la-gift' aria-hidden='true'></i> بدون کسر از کیف پول
            </span>
          ) : perSms !== undefined && sms && !isSmsClosed(sms) ? (
            <>
              <span>
                <i className='las la-coins' aria-hidden='true'></i> هر پیامک {fa(perSms)} ریال
              </span>
              {recipients !== undefined && (
                <span>
                  <i className='las la-users' aria-hidden='true'></i>
                  {count > 0
                    ? `برای ${fa(count)} گیرنده ${fa(perSms * count)} ریال`
                    : 'هنوز گیرنده‌ای ندارد'}
                </span>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ---- outcomes ----------------------------------------------------------------------------------------------

const SMS_BADGES: Record<string, string> = {
  none: 'badge-light',
  pending: 'badge-light-warning',
  sent: 'badge-light-success',
  skipped: 'badge-light-dark',
  failed: 'badge-light-danger',
}

const SMS_ICONS: Record<string, string> = {
  none: 'la-minus-circle',
  pending: 'la-hourglass-half',
  sent: 'la-check-circle',
  skipped: 'la-forward',
  failed: 'la-times-circle',
}

/** An SMS's state as a badge with an icon and its text, its reason and its cost. */
export const SmsOutcomeView: FC<{sms?: SmsOutcome | null; compact?: boolean}> = ({
  sms,
  compact,
}) => {
  if (!sms) return <span className='text-muted'>—</span>
  return (
    <div className='bsv-outcome'>
      <span className={clsx('badge', SMS_BADGES[sms.state] || 'badge-light')}>
        <i className={`las ${SMS_ICONS[sms.state] || 'la-sms'} me-1`} aria-hidden='true'></i>
        {sms.title}
      </span>
      {sms.reason && <span className='text-muted fs-8'>{sms.reason}</span>}
      {!compact && sms.state === 'sent' && (
        <span className='text-muted fs-8'>
          {fa(sms.parts)} بخش، {fa(sms.recipients)} گیرنده،{' '}
          {sms.cost > 0 ? `${fa(sms.cost)} ریال` : 'بدون هزینه'}
        </span>
      )}
    </div>
  )
}

/** The service tariff an alert or a run took: charged, unpaid (not delivered) or free. */
export const FeeView: FC<{fee?: ServiceFee | null}> = ({fee}) => {
  if (!fee || (fee.state === 'free' && !fee.amount) || hidesPayments()) return null
  if (fee.state === 'unpaid') {
    return (
      <span className='badge badge-light-danger'>
        <i className='las la-exclamation-circle me-1' aria-hidden='true'></i>
        هزینهٔ سرویس پرداخت نشد
      </span>
    )
  }
  if (fee.state === 'charged')
    return <span className='text-muted fs-8'>هزینهٔ سرویس: {fa(fee.amount)} ریال</span>
  return null
}

// ---- read-only -----------------------------------------------------------------------------------------------

/** Why a user sees a service but cannot change it, and who can give them «مدیریت سرویس‌ها و اعلان‌ها». */
export const SERVICE_READ_ONLY_TEXT =
  'برای تغییر این بخش، دسترسی «مدیریت سرویس‌ها و اعلان‌ها» لازم است. مدیر کسب‌وکار می‌تواند آن را در «کاربران و دسترسی‌ها» یا «اعلان‌ها و مسئولیت‌ها» به شما بدهد.'

export const ServiceReadOnlyNote: FC<{className?: string}> = ({className = 'mb-0'}) => (
  <div className={clsx('alert alert-light-info', className)} role='note'>
    <i className='las la-eye me-1' aria-hidden='true'></i>
    {SERVICE_READ_ONLY_TEXT}
  </div>
)

// ---- one-time fee confirmation -------------------------------------------------------------------------------

/** `perUse`: a fee of every use (a new buyer link), not a one-time activation. */
export type ChargePrompt = {amount: number; items: ChargeItem[]; message?: string; perUse?: boolean}

/** The dialog's title: a fee of each use, a one-time activation, a subscription or its renewal. */
export const chargeTitle = (prompt: ChargePrompt): string => {
  if (prompt.perUse) return 'تأیید هزینهٔ سرویس'
  const kind = chargeKind(prompt.items)
  return kind === 'renewal' ? 'تأیید تمدید اشتراک' : kind === 'subscription' ? 'تأیید هزینهٔ اشتراک' : 'تأیید هزینهٔ فعال‌سازی'
}

/** «افزایش موجودی و …» in a fee's confirmation: the step's name, and the step to go on with after the payment. */
export type WalletStep = {walletAction?: string; walletResume?: WalletResumeStep}

/**
 * What the fee buys and the amount; a one-time activation says it is taken once, a subscription (review 2026-10-16)
 * that it lasts its period and must then be renewed. Under it the balance; when it is short, the shortfall is offered
 * as a charge through the gateway (user request 2026-10-17).
 */
const ChargeText: FC<{prompt: ChargePrompt; balance?: number; action?: string} & WalletStep> = ({
  prompt,
  balance,
  action,
  walletAction,
  walletResume,
}) => {
  const {currentUser} = useAuth()
  const companyId = Number(currentUser?.cpi || 0)
  const short = balance !== undefined && balance < prompt.amount
  const names = prompt.items.map((item) => `«${item.label}»`).join('، ')
  const kind = chargeKind(prompt.items)
  return (
    <>
      <p className='mb-3'>
        {prompt.perUse ? (
          <>
            {action || 'این کار'} مبلغ <strong>{fa(prompt.amount)} ریال</strong> هزینهٔ سرویس{' '}
            {names} از کیف پول کسب‌وکار کم می‌کند.
          </>
        ) : (
          <>
            {names || '«این سرویس»'} مبلغ <strong>{fa(prompt.amount)} ریال</strong> از کیف پول
            کسب‌وکار کم می‌کند.{' '}
            {kind === 'activation'
              ? 'این هزینه فقط یک بار گرفته می‌شود و خاموش و روشن کردن تنظیمات سرویس هزینه‌ای ندارد؛ فقط اگر خود سرویس را در «سرویس‌ها و تعرفه‌ها» غیرفعال کنید، فعال‌سازی دوباره طبق تعرفهٔ همان روز است.'
              : 'اشتراک برای همان دوره معتبر است و پس از پایان آن، برای ادامه باید تمدید شود.'}
          </>
        )}
      </p>
      {(prompt.items.length > 1 || prompt.items.some((item) => item.note)) && (
        <ul className='mb-3'>
          {prompt.items.map((item) => (
            <li key={`${item.feature}-${item.label}`}>
              {item.label}: {fa(item.amount)} ریال
              {item.note && item.baseAmount !== undefined && item.baseAmount > item.amount && (
                <span className='text-success fs-8 ms-2'>
                  (به جای <s>{fa(item.baseAmount)}</s> ریال، {item.note})
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {balance !== undefined && (
        <div className={clsx('fs-7', short ? 'text-danger' : 'text-muted')}>
          موجودی کیف پول: {fa(balance)} ریال
          {short ? ' — کمتر از این هزینه است.' : ''}
          {short && companyId > 0 && (
            <div className='mt-2'>
              <WalletShortfallAction
                companyId={companyId}
                balance={balance}
                required={prompt.amount}
                actionLabel={walletAction || 'ادامه'}
                resume={walletResume}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}

type InlineChargeConfirmProps = {
  prompt: ChargePrompt
  balance?: number
  busy?: boolean
  action?: string
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
} & WalletStep

/**
 * The same confirmation inside a form that is already in a dialog (the buyer-link window): a box above the form's
 * buttons instead of a second dialog. In the Bazaar and Myket builds of the Android app no fee is taken: the box only
 * says so (hidesPayments).
 */
export const InlineChargeConfirm: FC<InlineChargeConfirmProps> = (props) =>
  hidesPayments() ? (
    <div className='alert alert-light-warning d-flex flex-wrap align-items-center gap-2 mt-4 mb-0' role='alert'>
      <span className='flex-grow-1'>{STORE_BUILD_UNAVAILABLE_TEXT}</span>
      <button type='button' className='btn btn-sm btn-light' onClick={props.onCancel}>
        بستن
      </button>
    </div>
  ) : (
    <InlineChargeConfirmBox {...props} />
  )

const InlineChargeConfirmBox: FC<InlineChargeConfirmProps> = ({prompt, balance, busy, action, confirmLabel = 'تأیید و پرداخت', onConfirm, onCancel, walletAction, walletResume}) => {
  const short = balance !== undefined && balance < prompt.amount
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)
  const confirm = useRef<HTMLButtonElement>(null)
  const focused = useRef(false)
  // The button that asked is disabled meanwhile: the confirmation is brought into view and takes the focus once its
  // button can be pressed.
  useEffect(() => {
    box.current?.scrollIntoView?.({block: 'nearest'})
  }, [])
  useEffect(() => {
    if (!busy && !focused.current) {
      focused.current = true
      confirm.current?.focus()
    }
  }, [busy])
  return (
    <div
      ref={box}
      className='alert alert-light-primary border border-primary border-dashed mt-4 mb-0'
      role='alertdialog'
      aria-labelledby={titleId}
    >
      <h4 id={titleId} className='fs-6 fw-bold mb-2'>
        {chargeTitle(prompt)}
      </h4>
      <ChargeText prompt={prompt} balance={balance} action={action} walletAction={walletAction} walletResume={walletResume} />
      <div className='d-flex flex-wrap justify-content-end gap-2 mt-3'>
        <button type='button' className='btn btn-sm btn-light' onClick={onCancel} disabled={busy}>
          انصراف
        </button>
        {/* A short wallet is charged first (the shortfall's own button above); the fee cannot be taken yet. */}
        {!short && (
          <button ref={confirm} type='button' className='btn btn-sm btn-primary' onClick={onConfirm} disabled={busy}>
            {busy && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>}
            {confirmLabel}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Before a save charges a one-time fee (409 BUSINESS_SERVICE_CHARGE_CONFIRM): what is activated, the amount and
 * the wallet's balance; «تأیید و پرداخت» resends the save with confirmCharge.
 */
export const ChargeConfirmDialog: FC<
  {
    prompt: ChargePrompt
    balance?: number
    busy?: boolean
    action?: string
    onConfirm: () => void
    onCancel: () => void
  } & WalletStep
> = ({prompt, balance, busy, action, onConfirm, onCancel, walletAction, walletResume}) => {
  if (hidesPayments()) return <StoreBuildUnavailableDialog onClose={onCancel} />
  const short = balance !== undefined && balance < prompt.amount
  return (
    <Modal
      show
      centered
      onHide={busy ? () => undefined : onCancel}
      aria-labelledby='bsv-charge-title'
    >
      <Modal.Header closeButton={!busy} closeLabel='بستن'>
        <Modal.Title id='bsv-charge-title' as='h2' className='fs-4'>
          {chargeTitle(prompt)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ChargeText prompt={prompt} balance={balance} action={action} walletAction={walletAction} walletResume={walletResume} />
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onCancel} disabled={busy}>
          انصراف
        </button>
        {/* A short wallet is charged first (the shortfall's own button above); the fee cannot be taken yet. */}
        {!short && (
          <button type='button' className='btn btn-primary' onClick={onConfirm} disabled={busy}>
            {busy && (
              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
            )}
            تأیید و پرداخت
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

// ---- chips -------------------------------------------------------------------------------------------------

/** A set of toggle buttons for a multi-choice: none pressed means «همه» when `allLabel` is given. */
export const ToggleChips: FC<{
  label: string
  options: Array<{value: string; label: string}>
  selected: string[]
  disabled?: boolean
  allLabel?: string
  onChange: (next: string[]) => void
}> = ({label, options, selected, disabled, allLabel, onChange}) => (
  <div className='bsv-chips' role='group' aria-label={label}>
    {allLabel && (
      <button
        type='button'
        className={clsx('bsv-chip', {'is-on': selected.length === 0})}
        aria-pressed={selected.length === 0}
        disabled={disabled}
        onClick={() => onChange([])}
      >
        {allLabel}
      </button>
    )}
    {options.map((option) => {
      const on = selected.includes(option.value)
      return (
        <button
          key={option.value}
          type='button'
          className={clsx('bsv-chip', {'is-on': on})}
          aria-pressed={on}
          disabled={disabled}
          onClick={() =>
            onChange(
              on ? selected.filter((value) => value !== option.value) : [...selected, option.value]
            )
          }
        >
          {on && <i className='las la-check me-1' aria-hidden='true'></i>}
          {option.label}
        </button>
      )
    })}
  </div>
)

// ---- success toast -----------------------------------------------------------------------------------------

/** A short success message at the top of the screen; it closes by itself. */
export const SuccessToast: FC<{message?: string; onClose: () => void}> = ({message, onClose}) => (
  <div
    className='toast-container position-fixed top-0 start-50 translate-middle-x p-3'
    style={{zIndex: 1100}}
    aria-live='polite'
    aria-atomic='true'
  >
    <Toast show={Boolean(message)} onClose={onClose} delay={4000} autohide bg='success'>
      <Toast.Body className='text-white d-flex align-items-center gap-2 fs-6'>
        <i className='las la-check-circle fs-2' aria-hidden='true'></i>
        <span>{message}</span>
      </Toast.Body>
    </Toast>
  </div>
)

// ---- switch ------------------------------------------------------------------------------------------------

/** An on/off setting: the switch, its name and a line that says what it does. */
export const SwitchField: FC<{
  id: string
  checked: boolean
  disabled?: boolean
  label: ReactNode
  hint?: ReactNode
  className?: string
  onChange: (checked: boolean) => void
}> = ({id, checked, disabled, label, hint, className, onChange}) => (
  <div
    className={clsx(
      'bsv-switch form-check form-switch form-check-custom form-check-solid',
      className
    )}
  >
    <input
      id={id}
      type='checkbox'
      role='switch'
      className='form-check-input'
      checked={checked}
      disabled={disabled}
      aria-describedby={hint ? `${id}-hint` : undefined}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className='bsv-switch__text'>
      <label className='form-check-label fw-semibold' htmlFor={id}>
        {label}
      </label>
      {hint && <small id={`${id}-hint`}>{hint}</small>}
    </span>
  </div>
)
